import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The Environment each world draws with. Godot's World3D takes the Environment of the first
 * WorldEnvironment in its group, which only a node with a valid Environment joins
 * (`world_environment.cpp:40-41`, `:77-83`). One chooser per world applies it (ADR-0025). A viewport
 * pass draws with its camera's own (`ViewportPassEnvironment`). The CameraAttributes choose apart
 * (`worldExposure.tsx`).
 */
import { createContext, useContext, useMemo, useState } from 'react';
import * as THREE from 'three';
import { createPortal } from '@react-three/fiber';
import { DrawnEnvironmentLayer, EnvironmentLayer } from './EnvironmentLayer';
import { NoEnvironmentLayer } from './NoEnvironmentLayer';
import { Compositor } from './Compositor';
import { useDraws3D } from './draws3D';
import { PreviewEnvironment } from '../preview/PreviewEnvironment';
import { previewEnvironment } from '../preview/godotPreviewLighting';
import { PreviewSun } from '../preview/PreviewSun';
import { usePreviewYield } from '../preview/usePreviewYield';
import { WorldKindProvider } from './WorldKindContext';
import { SharedWorldExposureScope, WorldAttributesExposure, WorldCamerasScope, WorldExposureScope, usePassCameraEnvironment, usePassCameraEnvironments, useRenderCameraEnvironment, } from './worldExposure';
import { createWorldGroup } from './worldGroup';
import { PassToneMapProvider } from './passToneMap';
import { CanvasWorkspaceProvider } from '../contexts/CanvasWorkspaceContext';
import { useSceneShadowFit } from '../SceneShadowFitter';
import { useSceneDepthPrepass } from '../depthPrepass/SceneDepthPrepass';
import { CanvasWorldContext } from './canvasWorldScene';
import { identityKey } from '../../utils/identityKey';
import { useOwnEnvironment } from './sceneEnvironment';
const environments = createWorldGroup();
export const usePublishEnvironment = environments.usePublish;
/** The world's own Environment, which a pass through a camera with none draws. Null for none. */
const WorldOwnEnvironmentContext = createContext(null);
/**
 * One viewport's world: the WorldEnvironments inside publish to it, and its choosers apply the
 * winners. The Environment and the CameraAttributes choose apart.
 */
export function WorldEnvironmentScope({ world, children }) {
    return (_jsx(WorldKindProvider, { world: world, children: _jsx(environments.Provider, { children: _jsx(WorldExposureScope, { children: world === 'root' ? (_jsx(RootEnvironment, { children: children })) : (_jsx(SubViewportEnvironment, { children: children })) }) }) }));
}
/**
 * The world of a 2D canvas. Its viewport has no camera into it, so Godot draws none of it there
 * (`renderer_viewport.cpp:362`). Only its shared viewports draw it: one scene for all of them, with
 * the world's Environment, its shadow fit and its depth prepass applied once while one holds it.
 */
export function CanvasWorldScope({ children }) {
    const scene = useMemo(() => {
        const created = new THREE.Scene();
        created.name = 'Canvas::world';
        return created;
    }, []);
    const [holders, setHolders] = useState(0);
    const world = useMemo(() => ({
        scene,
        hold: () => {
            setHolders((count) => count + 1);
            return () => setHolders((count) => count - 1);
        },
    }), [scene]);
    const drawnScene = holders > 0 ? scene : null;
    useSceneShadowFit(drawnScene);
    useSceneDepthPrepass(drawnScene);
    return (_jsx(environments.Provider, { children: _jsx(WorldCamerasScope, { children: _jsx(CanvasWorldContext.Provider, { value: world, children: _jsx(CanvasWorldEnvironment, { scene: drawnScene, children: children }) }) }) }));
}
/** The canvas world's Environment, drawn into its scene only while that scene is drawn. */
function CanvasWorldEnvironment({ scene, children }) {
    const own = resolvedEnvironment(environments.useChosen());
    // The layer draws into the world's scene as a 3D view does, whatever the canvas draws, at the
    // exposure of the world's own CameraAttributes, since the viewports that draw it share one.
    const layers = scene &&
        createPortal(_jsx(CanvasWorkspaceProvider, { workspace: "3d", children: _jsx(WorldKindProvider, { world: "subViewport", children: _jsxs(WorldAttributesExposure, { children: [own && _jsx(EnvironmentLayer, { ...own }), _jsx(DrawnEnvironments, { shown: own, own: own })] }) }) }), scene);
    return (_jsxs(WorldOwnEnvironmentContext.Provider, { value: own, children: [layers, children] }));
}
/**
 * A sub-viewport's portal into its parent's world, which only a 2D canvas portals. Godot keys a
 * world's WorldEnvironment group by its scenario (`world_environment.cpp:76-85`), so a node inside
 * publishes to the parent's chooser, whose first node in tree order wins for both. The canvas's
 * world draws its Environments into the shared scene.
 */
export function SharedWorldEnvironmentScope({ children }) {
    return (_jsx(WorldKindProvider, { world: "subViewport", children: _jsx(SharedWorldExposureScope, { children: children }) }));
}
/**
 * The root viewport's Environment, which falls back to the editor preview environment, and the
 * editor preview sun after the scene, so the scene's own nodes come first. One yield serves both,
 * since each walks the whole live tree. One compositor tonemaps every 3D frame of the canvas with
 * whichever Environment applies, so a switch or a load keeps its buffer (ADR-0061).
 */
function RootEnvironment({ children }) {
    const authored = useViewEnvironment();
    const chosen = environments.useChosen();
    const preview = usePreviewYield();
    const draws3D = useDraws3D();
    // An authored Environment proves a WorldEnvironment node or a camera's, and takes the preview away.
    const showsPreview = !authored && preview.environment;
    const shown = showsPreview ? previewEnvironment() : resolvedEnvironment(authored);
    // The preview stands in for a WorldEnvironment, so a pass through a camera with none draws it too.
    const own = !chosen && preview.environment ? previewEnvironment() : resolvedEnvironment(chosen);
    return (_jsx(WorldOwnEnvironmentContext.Provider, { value: own, children: _jsxs(PassToneMapProvider, { settings: shown?.settings ?? null, glowMap: shown?.glowMap ?? null, children: [draws3D && _jsx(Compositor, {}), _jsx(RootEnvironmentLayer, { authored: authored, showsPreview: showsPreview }), _jsx(DrawnEnvironments, { shown: shown, own: own }), children, preview.sun && _jsx(PreviewSun, {})] }) }));
}
function RootEnvironmentLayer({ authored, showsPreview, }) {
    if (authored?.status === 'resolved')
        return _jsx(EnvironmentLayer, { ...authored.environment });
    if (authored?.status === 'pending')
        return null;
    return showsPreview ? _jsx(PreviewEnvironment, {}) : _jsx(NoEnvironmentLayer, {});
}
/**
 * A sub-viewport's portal world. Its pass sets its own clear colour, so only a resolved Environment
 * adds a layer here, and a missing one leaves the root's clear colour alone.
 */
function SubViewportEnvironment({ children }) {
    const shown = resolvedEnvironment(useViewEnvironment());
    const own = resolvedEnvironment(environments.useChosen());
    // One tree shape in either state, so a resolve never remounts the portal.
    return (_jsxs(WorldOwnEnvironmentContext.Provider, { value: own, children: [shown && _jsx(EnvironmentLayer, { ...shown }), _jsx(DrawnEnvironments, { shown: shown, own: own }), children] }));
}
/**
 * Gives the tonemap stage of a viewport pass, below, the Environment the pass draws with. It reads
 * the camera the enclosing `ViewportPassAttributes` elects.
 *
 * @canonical The Environment a viewport pass draws with
 */
export function ViewportPassEnvironment({ children }) {
    const drawn = passEnvironmentOf(usePassCameraEnvironment(), useContext(WorldOwnEnvironmentContext));
    return (_jsx(PassToneMapProvider, { settings: drawn?.settings ?? null, glowMap: drawn?.glowMap ?? null, children: children }));
}
/**
 * Whether a camera's Environment replaces its world's: once it resolves
 * (`renderer_scene_cull.cpp:3594-3597`). While it loads the world's holds, since Godot has no load
 * gap to show.
 */
function replacesWorld(camera) {
    return camera?.status === 'resolved';
}
/** The Environment a pass through `camera` draws its world with, null for none. */
function passEnvironmentOf(camera, own) {
    return replacesWorld(camera) ? camera.environment : own;
}
/**
 * Mounts each Environment a pass or a probe capture of the world draws with that its scene does not
 * show, and names the world's own on the scene (`drawAsWorld`). Each holds the exposure the scene
 * holds, as the shown one does, and a draw puts its own on (`drawWith`).
 */
function DrawnEnvironments({ shown, own, }) {
    useOwnEnvironment(own?.settings ?? null);
    const cameras = usePassCameraEnvironments();
    const layers = useMemo(() => {
        const unique = new Map();
        for (const drawn of [own, ...cameras.map((camera) => passEnvironmentOf(camera, own))]) {
            if (drawn && drawn.settings !== shown?.settings)
                unique.set(drawn.settings, drawn);
        }
        return [...unique.values()];
    }, [cameras, own, shown]);
    return layers.map((environment) => (_jsx(DrawnEnvironmentLayer, { ...environment }, identityKey(environment.settings))));
}
/** The Environment a viewport renders with: its camera's, else the world's (`replacesWorld`). */
function useViewEnvironment() {
    const camera = useRenderCameraEnvironment();
    const world = environments.useChosen();
    return replacesWorld(camera) ? camera : world;
}
function resolvedEnvironment(authored) {
    return authored?.status === 'resolved' ? authored.environment : null;
}
//# sourceMappingURL=WorldEnvironmentScope.js.map